import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";
import CartPole from "./cartpole";
import Latex from "react-latex-next";
import "./pid.css";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { Arrow, Circle, Layer, Line, Rect, Stage } from "react-konva";
import Konva from "konva";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";

const poleRadisUnit = 10;
const cartHeightUnit = 50;
const cartWidthUnit = 100;

function Main() {
  const [cartPole, setCartPole] = useState(new CartPole());
  const [l, setL] = useState(1.5);
  const [mc, setMc] = useState(1);
  const [mp, setMp] = useState(2);
  const [kpTheta, setKpTheta] = useState(0);
  const [kiTheta, setKiTheta] = useState(0);
  const [kdTheta, setKdTheta] = useState(0);
  const [kpX, setKpX] = useState(0);
  const [kiX, setKiX] = useState(0);
  const [kdX, setKdX] = useState(0);
  const [xOffset, setXOffset] = useState(0);
  const [showForce, setShowForce] = useState(true);

  const reset = () => {
    setMc(1);
    setL(1.5);
    setMp(2);
    setKpTheta(0);
    setKiTheta(0);
    setKdTheta(0);
    setKpX(0);
    setKiX(0);
    setKdX(0);
    setXOffset(0);
    setCartPole(new CartPole());
  };

  useEffect(() => {
    cartPole.l = l;
    cartPole.mc = mc;
    cartPole.mp = mp;
    cartPole.kpTheta = kpTheta;
    cartPole.kiTheta = kiTheta;
    cartPole.kdTheta = kdTheta;
    cartPole.kpX = kpX;
    cartPole.kiX = kiX;
    cartPole.kdX = kdX;
    cartPole.xOffset = xOffset;
    setCartPole(cartPole);
  }, [l, mc, mp, kpTheta, kiTheta, kdTheta, kpX, kiX, kdX, xOffset]);

  const divRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const cartRef: React.RefObject<Konva.Rect> = useRef();
  const linkRef: React.RefObject<Konva.Line> = useRef();
  const poleRef: React.RefObject<Konva.Circle> = useRef();
  const forceRef: React.RefObject<Konva.Arrow> = useRef();

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    var anim = new Konva.Animation((frame) => {
      cartPole.tick(1 / 60);
      let cords = cartPole.coordinates;
      if (!isNaN(cartPole.fx)) {
        cartRef.current.x(cords.x);
        if (cords.x > dimensions.width || cords.x < -dimensions.width)
          setOpen(true);
        else setOpen(false);

        cartRef.current.width(cartWidthUnit * cartPole.mc);
        cartRef.current.height(cartHeightUnit * cartPole.mc);
        cartRef.current.offsetX((cartWidthUnit * cartPole.mc) / 2);
        cartRef.current.offsetY((cartHeightUnit * cartPole.mc) / 2);
        linkRef.current.points([cords.x, 0, cords.bx, cords.by]);
        poleRef.current.x(cords.bx);
        poleRef.current.y(cords.by);
        poleRef.current.radius(poleRadisUnit * cartPole.mp);
        forceRef.current.points([
          cords.x,
          0,
          cords.x + cartPole.fx * cartPole.mtocenti,
          0,
        ]);
      }
    }, cartRef.current.getLayer());

    anim.start();
    return () => {
      anim.stop();
    };
  }, [cartPole, dimensions]);

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
    }
  }, [divRef]);

  useEffect(() => {
    if (showForce) forceRef.current.show();
    else forceRef.current.hide();
  }, [showForce]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls
          setKpTheta={setKpTheta}
          setKdTheta={setKdTheta}
          cartPole={cartPole}
          reset={reset}
          setKpX={setKpX}
          setKdX={setKdX}
          setKiX={setKiX}
          setXOffset={setXOffset}
        />
      </Box>
      <Box
        sx={{
          flex: 13,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Cart-Pole is out of Screen"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Try again! You can choose restart at your configuration or reset
              the parameters.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                reset();
                cartPole.restart();
                handleClose();
              }}
            >
              reset
            </Button>
            <Button
              onClick={() => {
                cartPole.restart();
                handleClose();
              }}
              autoFocus
            >
              restart
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          >
            <Typography sx={{ marginBottom: 2 }}>
              Parameters of Cart-Pole
            </Typography>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$m_c (kg)$</Latex>
              </label>
              <input
                value={mc}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setMc(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$m_p (kg)$</Latex>
              </label>
              <input
                value={mp}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setMp(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$l (m)$</Latex>
              </label>
              <input
                value={l}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setL(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>{`$x_{o} (m)$`}</Latex>
              </label>
              <input
                value={xOffset}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setXOffset(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          >
            <Typography sx={{ marginBottom: 2 }}>
              Parameters of Angle PID
            </Typography>

            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$k_p^\theta$</Latex>
              </label>
              <input
                value={kpTheta}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setKpTheta(parseFloat(e.target.value));
                }}
                step={1}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$k_d^\theta$</Latex>
              </label>
              <input
                value={kdTheta}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setKdTheta(parseFloat(e.target.value));
                }}
                step={1}
                type="number"
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          >
            <Typography sx={{ marginBottom: 2 }}>
              Parameters of Position PID
            </Typography>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$k_p^x$</Latex>
              </label>
              <input
                value={kpX}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setKpX(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$k_i^x$</Latex>
              </label>
              <input
                value={kiX}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setKiX(parseFloat(e.target.value));
                }}
                step={0.01}
                type="number"
              />
            </div>
            <div className="controlpid">
              <label style={{ marginRight: 10 }}>
                <Latex>$k_d^x$</Latex>
              </label>
              <input
                value={kdX}
                onChange={(e) => {
                  if (!isNaN(parseFloat(e.target.value)))
                    setKdX(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              marginRight: 5,
            }}
          >
            <Button
              sx={{ marginBottom: 2 }}
              variant="contained"
              onClick={() => {
                reset();
                cartPole.restart();
              }}
            >
              Reset
            </Button>
            <Button variant="contained" onClick={() => cartPole.restart()}>
              Restart
            </Button>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showForce}
                    onChange={(e) => setShowForce(e.target.checked)}
                  />
                }
                label="Show Force"
              />
            </FormGroup>
          </Box>
        </Box>
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          scaleY={-1}
          offsetX={-dimensions.width / 2}
          offsetY={dimensions.height / 2 + 125}
        >
          <Layer>
            <Line
              points={[-dimensions.width / 2, 0, dimensions.width / 2, 0]}
              strokeWidth={10}
              stroke={"grey"}
            />
            <Rect
              x={0}
              y={0}
              width={100}
              height={50}
              offsetX={50}
              offsetY={25}
              fill="black"
              ref={cartRef}
            />
            <Line strokeWidth={10} stroke={"grey"} ref={linkRef} />
            <Circle radius={15} fill="orange" x={0} y={175} ref={poleRef} />
            <Circle radius={10} fill="black" x={0} y={0} />
            <Arrow
              x={0}
              y={0}
              pointerLength={20}
              pointerWidth={20}
              points={[
                cartPole.coordinates.x,
                0,
                cartPole.coordinates.x +
                  (isNaN(cartPole.fx) ? 0 : cartPole.fx) * cartPole.mtocenti,
                0,
              ]}
              stroke={"#33ccff"}
              strokeWidth={4}
              fill={"#33ccff"}
              ref={forceRef}
            />
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function PID(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of PID and Cart-Pole</title>
        <meta
          name="description"
          content="Interactive PID Control for Cart-Pole: Tune mass, length, and PID parameters for angle and position. Follow a step-by-step tutorial to stabilize the system, visualize forces, and prevent oscillation. Perfect for learning PID control principles."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default PID;
